import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

// Bottom slide-up sheet on all screens. Tap backdrop or X to close.
export default function SettingsSheet({ open, onClose, onResetOnboarding }) {
  const [reverbOn, setReverbOn] = useState(false);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
      data-testid="settings-sheet"
      className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center"
    >
      <button
        aria-label="Close settings"
        onClick={onClose}
        data-testid="settings-backdrop"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, hsla(36,12%,18%,0.4), hsla(36,12%,18%,0.55))",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      />
      <div
        className="relative z-10 w-full max-w-md cg-card rounded-t-[2rem] sm:rounded-[2rem] p-6 sm:p-7"
        style={{ boxShadow: "0 30px 80px -30px hsla(36,10%,9%,0.5)" }}
      >
        <div className="mb-4 flex items-start justify-between">
          <h3
            id="settings-title"
            className="font-serif-display text-2xl sm:text-3xl"
          >
            Settings
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="settings-close"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border"
            style={{
              borderColor: "hsla(38,25%,80%,0.7)",
              color: "hsl(30 9% 30%)",
              background: "hsla(0,0%,100%,0.7)",
            }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <section className="space-y-5">
          <div className="rounded-2xl p-4" style={{ background: "hsla(38,30%,94%,0.7)" }}>
            <h4 className="font-serif-display text-lg">About this voice</h4>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              The voice on the line is AI-generated. It can speak in a sacred
              style and help you reflect, but it is not literal divine
              authority. Read more in our{" "}
              <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">
                privacy policy
              </Link>
              .
            </p>
          </div>

          <div
            className="flex items-center justify-between rounded-2xl p-4"
            style={{ background: "hsla(38,30%,94%,0.7)" }}
          >
            <div>
              <div className="text-sm font-medium text-foreground">Voice treatment</div>
              <div className="text-xs text-muted-foreground">
                Subtle reverb ({reverbOn ? "on" : "off"}) — coming soon
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={reverbOn}
              data-testid="settings-reverb-toggle"
              onClick={() => setReverbOn((v) => !v)}
              className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors"
              style={{
                background: reverbOn ? "hsl(41 60% 55%)" : "hsl(38 20% 78%)",
              }}
            >
              <span
                aria-hidden
                className="inline-block h-5 w-5 rounded-full bg-white shadow transition-transform"
                style={{ transform: reverbOn ? "translateX(22px)" : "translateX(4px)" }}
              />
            </button>
          </div>

          <button
            type="button"
            onClick={onResetOnboarding}
            data-testid="settings-reset"
            className="cg-btn-ghost w-full !min-h-[44px]"
          >
            Reset onboarding
          </button>
        </section>
      </div>
    </div>
  );
}
